import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";
import { get_client_onboarding_user } from "../../../config/client_api_calls";
import HelperClass from "../../../config/helperClass";

const auth = new HelperClass();

// Colors matched exactly from formRenderClient.js tab stepper
const TEAL        = "#1ee0ac"; // active circle bg + bottom border
const TEAL_BG     = "#edfaf6"; // completed circle background
const DARK_TEXT   = "#0d1b38"; // active circle text + active label
const IDLE_CIRCLE = "#e4e9f2"; // inactive circle background
const IDLE_TEXT   = "#6b7c95"; // inactive circle text + inactive label
const BORDER      = "#dce2ec"; // bar bottom border

export default forwardRef(function OnboardingTopBar(
  {
    currentStep,
    onStepChange,
    clientAccountNo,
    onFormTokenChange,
    onFormChange,
    onStepsDataUpdate,
    onFormAccountChange,
    onAllowSkipChange,
    onStepTitleChange,
    onOnboardingTitleChange,
  },
  ref
) {
  const [activeStep, setActiveStep] = useState(0);
  const [steps, setSteps] = useState([]);
  const [stepsData, setStepsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completed, setCompleted] = useState(new Set());
  const [skipped, setSkipped] = useState(new Set());
  const [maxStepReached, setMaxStepReached] = useState(-1);
  const isFetchingRef = useRef(false);
  const lastFetchedAccountRef = useRef(null);
  const scrollRef = useRef(null);

  // ── Fetch helpers (identical logic to OnboardingSidebar) ──────────────────

  const applyStepsData = (enabledSteps, callbacks = true) => {
    setSteps(enabledSteps.map((s) => s.title));
    setStepsData(enabledSteps);

    const completedSet = new Set();
    const skippedSet = new Set();
    let firstIncomplete = 0;
    let found = false;

    enabledSteps.forEach((s, i) => {
      if (s.isFilled === 1 || s.isFilled === true) completedSet.add(i);
      if (s.isSkipped === 1 || s.isSkipped === true) skippedSet.add(i);
      if (
        !found &&
        (s.isFilled === 0 || s.isFilled === false) &&
        (s.isSkipped === 0 || s.isSkipped === false)
      ) {
        firstIncomplete = i;
        found = true;
      }
    });

    setCompleted(completedSet);
    setSkipped(skippedSet);
    setActiveStep(firstIncomplete);

    if (callbacks) {
      if (onStepChange) onStepChange(firstIncomplete);
      const stepData = enabledSteps[firstIncomplete];
      if (stepData) {
        if (onFormTokenChange) onFormTokenChange(stepData.formToken || "");
        if (onFormChange) onFormChange(stepData.form || {});
        if (onFormAccountChange) onFormAccountChange(stepData.form || "");
        if (onAllowSkipChange) onAllowSkipChange(stepData.allowSkip || 0);
        if (onStepTitleChange) onStepTitleChange(stepData.title || "");
      }
      if (onStepsDataUpdate) onStepsDataUpdate(enabledSteps);
    }
  };

  const fetchOnboardingSteps = async (accountNo = clientAccountNo) => {
    if (isFetchingRef.current || lastFetchedAccountRef.current === accountNo)
      return;
    isFetchingRef.current = true;
    lastFetchedAccountRef.current = accountNo;
    try {
      setLoading(true);
      if (!accountNo) return;

      const response = await get_client_onboarding_user(accountNo, "client");
      if (response && response.data) {
        const onboardingData = response.data.data || response.data;
        if (onOnboardingTitleChange) {
          onOnboardingTitleChange(
            onboardingData?.onboardingTitle || "",
            onboardingData?.message || ""
          );
        }

        let stepsArray = null;
        if (onboardingData && onboardingData.steps) {
          stepsArray = onboardingData.steps.steps || onboardingData.steps;
        }

        if (stepsArray && Array.isArray(stepsArray) && stepsArray.length > 0) {
          const enabledSteps = stepsArray.filter(
            (s) => s.isEnabled === 1 || s.isEnabled === true
          );
          if (enabledSteps.length > 0) {
            applyStepsData(enabledSteps, true);
          }
        }
      }
    } catch (err) {
      console.error("Error fetching onboarding steps:", err);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  };

  // ── Imperative handle (same interface as OnboardingSidebar ref) ───────────

  useImperativeHandle(ref, () => ({
    refreshSteps: async () => {
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;
      try {
        if (!clientAccountNo) return;
        const response = await get_client_onboarding_user(
          clientAccountNo,
          "client"
        );
        if (response && response.data) {
          const onboardingData = response.data.data || response.data;
          if (onOnboardingTitleChange) {
            onOnboardingTitleChange(
              onboardingData?.onboardingTitle || "",
              onboardingData?.message || ""
            );
          }
          let stepsArray = null;
          if (onboardingData && onboardingData.steps) {
            stepsArray = onboardingData.steps.steps || onboardingData.steps;
          }
          if (stepsArray && Array.isArray(stepsArray) && stepsArray.length > 0) {
            const enabledSteps = stepsArray.filter(
              (s) => s.isEnabled === 1 || s.isEnabled === true
            );
            if (enabledSteps.length > 0) {
              applyStepsData(enabledSteps, true);
            }
          }
        }
      } catch (err) {
        console.error("Error refreshing onboarding steps:", err);
      } finally {
        isFetchingRef.current = false;
      }
    },
    markStepCompleted: (stepIndex) => {
      setCompleted((prev) => {
        const next = new Set(prev);
        next.add(stepIndex);
        return next;
      });
    },
  }));

  // ── Effects ───────────────────────────────────────────────────────────────

  useEffect(() => {
    if (currentStep !== undefined && currentStep !== null) {
      setActiveStep(currentStep);
      setMaxStepReached((prev) => Math.max(prev, currentStep));
    }
  }, [currentStep]);

  useEffect(() => {
    fetchOnboardingSteps();
  }, [clientAccountNo]);

  // Scroll active step into the center of the bar
  useEffect(() => {
    if (loading) return;
    const bar = scrollRef.current;
    if (!bar) return;
    const t = setTimeout(() => {
      const active = bar.querySelector("[data-step-active='true']");
      if (!active) return;
      const br = bar.getBoundingClientRect();
      const ar = active.getBoundingClientRect();
      bar.scrollLeft = bar.scrollLeft + (ar.left - br.left) + ar.width / 2 - br.width / 2;
    }, 100);
    return () => clearTimeout(t);
  }, [activeStep, loading]);

  // ── Step logic (mirrors OnboardingSidebar exactly) ────────────────────────

  const handleStep = (step) => () => {
    if (!stepsData || stepsData.length <= step) return;
    const clicked = stepsData[step];
    const isFilled = clicked.isFilled === 1 || clicked.isFilled === true;
    const isSkippedStep = clicked.isSkipped === 1 || clicked.isSkipped === true;
    if (isFilled || isSkippedStep || step <= maxStepReached) {
      setActiveStep(step);
      setMaxStepReached((prev) => Math.max(prev, step));
      if (onStepChange) onStepChange(step);
      if (onFormTokenChange) onFormTokenChange(clicked.formToken || "");
      if (onFormChange) onFormChange(clicked.form || {});
      if (onFormAccountChange) onFormAccountChange(clicked.form || "");
      if (onAllowSkipChange) onAllowSkipChange(clicked.allowSkip || 0);
      if (onStepTitleChange) onStepTitleChange(clicked.title || "");
    }
  };

  const isStepComplete = (step) => !skipped.has(step) && completed.has(step);

  const isStepAccessible = (step) => {
    if (step === activeStep || step <= maxStepReached) return true;
    if (stepsData && stepsData.length > step) {
      const s = stepsData[step];
      return (
        s.isFilled === 1 ||
        s.isFilled === true ||
        s.isSkipped === 1 ||
        s.isSkipped === true
      );
    }
    return false;
  };

  const scrollTabLeft = () => {
    const bar = scrollRef.current;
    if (!bar) return;
    bar.scrollBy({ left: -bar.clientWidth * 0.75, behavior: "smooth" });
  };

  const scrollTabRight = () => {
    const bar = scrollRef.current;
    if (!bar) return;
    bar.scrollBy({ left: bar.clientWidth * 0.75, behavior: "smooth" });
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    // Skeleton strip mirroring the step tabs — part of the single cohesive
    // page skeleton (top bar + message + content) shown on first load.
    return (
      <>
        <style>{`
          .obs-topbar-skel { position: relative; overflow: hidden; background: #e9edf3; border-radius: 999px; flex-shrink: 0; }
          .obs-topbar-skel::after { content: ""; position: absolute; top: 0; left: 0; right: 0; bottom: 0; transform: translateX(-100%); background: linear-gradient(90deg, transparent, rgba(255,255,255,0.65), transparent); animation: obsTopbarShimmer 1.4s ease-in-out infinite; }
          @keyframes obsTopbarShimmer { 100% { transform: translateX(100%); } }
        `}</style>
        <div
          style={{
            background: "#F5F6FA",
            borderBottom: `1px solid ${BORDER}`,
            height: "52px",
            display: "flex",
            alignItems: "center",
            gap: "32px",
            padding: "0 24px",
            overflow: "hidden",
          }}
        >
          {[120, 96, 140, 104, 128, 90].map((w, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: "9px", flexShrink: 0 }}>
              <div className="obs-topbar-skel" style={{ width: "24px", height: "24px", borderRadius: "50%" }} />
              <div className="obs-topbar-skel" style={{ width: `${w}px`, height: "12px" }} />
            </div>
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      {/* Scoped: hide webkit scrollbar, hover tint */}
      <style>{`
        .obs-topbar-steps::-webkit-scrollbar { display: none; }
        .obs-tab-btn:hover { background: rgba(30,224,172,0.06) !important; }

        .obs-nav-arrow { display: none; }

        @media (max-width: 768px) {
          .obs-topbar-steps {
            padding: 0 !important;
            scroll-snap-type: x mandatory;
          }
          .obs-topbar-steps::before,
          .obs-topbar-steps::after {
            content: '';
            flex-shrink: 0;
            min-width: 12.5vw;
          }
          .obs-tab-btn {
            min-width: 75vw !important;
            scroll-snap-align: center;
            display: flex !important;
            justify-content: center !important;
            align-items: center !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
            border-bottom: none !important;
          }
          .obs-step-connector { display: none !important; }
          .obs-tab-circle {
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            text-align: center !important;
            line-height: 1 !important;
            padding: 0 !important;
            letter-spacing: 0 !important;
          }
          .obs-topbar-wrapper {
            border-bottom: 3px solid #1ee0ac !important;
            position: relative;
          }
          .obs-nav-arrow {
            display: flex;
            align-items: center;
            justify-content: center;
            position: absolute;
            top: 0;
            bottom: 3px;
            width: 40px;
            background: transparent;
            border: none;
            color: #b0bac8;
            font-size: 34px;
            line-height: 1;
            cursor: pointer;
            z-index: 2;
            padding: 0;
            outline: none;
            pointer-events: auto;
          }
          .obs-nav-arrow:active { color: #6b7c95; }
          .obs-nav-arrow-left  { left: 0; }
          .obs-nav-arrow-right { right: 0; }
        }
      `}</style>

      <div
        className="obs-topbar-wrapper"
        style={{
          background: "#F5F6FA",
          borderBottom: `1px solid ${BORDER}`,
          boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
        }}
      >
        <button type="button" className="obs-nav-arrow obs-nav-arrow-left" onClick={scrollTabLeft} aria-label="Previous step">&#8249;</button>
        <button type="button" className="obs-nav-arrow obs-nav-arrow-right" onClick={scrollTabRight} aria-label="Next step">&#8250;</button>
        <div
          ref={scrollRef}
          className="obs-topbar-steps"
          style={{
            display: "flex",
            alignItems: "center",
            padding: "0 24px",
            height: "52px",
            overflowX: "auto",
            scrollBehavior: "smooth",
            msOverflowStyle: "none",
            scrollbarWidth: "none",
          }}
        >
          {steps.map((label, index) => {
            const isActive = activeStep === index;
            const isDone = isStepComplete(index);
            const accessible = isStepAccessible(index);

            return (
              <React.Fragment key={index}>
                {/* Subtle separator between steps */}
                {index > 0 && (
                  <div
                    className="obs-step-connector"
                    style={{
                      flexShrink: 0,
                      width: "20px",
                      height: "1px",
                      background: BORDER,
                      alignSelf: "center",
                    }}
                  />
                )}

                {/* Tab button — exactly mirrors formRenderClient obs-tab-btn */}
                <button
                  type="button"
                  className="obs-tab-btn"
                  data-step-active={isActive ? "true" : "false"}
                  onClick={accessible ? handleStep(index) : undefined}
                  title={
                    !accessible
                      ? "Complete previous steps to unlock"
                      : undefined
                  }
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "9px",
                    padding: "14px 16px",
                    border: "none",
                    borderBottom: isActive
                      ? `3px solid ${TEAL}`
                      : "3px solid transparent",
                    background: "transparent",
                    cursor: accessible ? "pointer" : "default",
                    whiteSpace: "nowrap",
                    transition: "border-color 0.2s ease",
                    outline: "none",
                    flexShrink: 0,
                    opacity: accessible ? 1 : 0.45,
                  }}
                >
                  {/* Circle — mirrors obs-tab-circle */}
                  <span
                    className="obs-tab-circle"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "24px",
                      height: "24px",
                      minWidth: "24px",
                      borderRadius: "50%",
                      background: isDone ? TEAL_BG : isActive ? TEAL : IDLE_CIRCLE,
                      border: isDone ? `1.5px solid ${TEAL}` : "none",
                      color: isDone ? DARK_TEXT : isActive ? DARK_TEXT : IDLE_TEXT,
                      fontSize: "11px",
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    {isDone ? "✓" : index + 1}
                  </span>

                  {/* Label — mirrors obs-tab-label */}
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: 600,
                      letterSpacing: "0.01em",
                      color: isActive ? DARK_TEXT : IDLE_TEXT,
                    }}
                  >
                    {label}
                  </span>
                </button>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </>
  );
});
